import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { apiRequest } from '../utils/api';
import { useSearchParams } from 'react-router-dom';

interface QuickBooksStatus {
  connected: boolean;
  realmId: string | null;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
}

const ALLOWED_EMAILS = ['vladimir@glasscompanyny.com', 'janryanituralde@glasscompanyny.com'];

export default function Integrations() {
  const userEmail = localStorage.getItem('userEmail') || '';
  const canControl = ALLOWED_EMAILS.includes(userEmail);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [status, setStatus] = useState<QuickBooksStatus | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Check for OAuth callback results
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const realmId = searchParams.get('realmId');

    if (success === 'true') {
      setSnackbar({
        open: true,
        message: `QuickBooks connected successfully! Realm ID: ${realmId || 'N/A'}`,
        severity: 'success',
      });
      // Clear URL parameters
      setSearchParams({});
      // Refresh status
      loadStatus();
    } else if (error) {
      setSnackbar({
        open: true,
        message: `QuickBooks connection failed: ${decodeURIComponent(error)}`,
        severity: 'error',
      });
      // Clear URL parameters
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Load QuickBooks connection status
  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/quickbooks/status', { method: 'GET' });
      setStatus(response.data);
    } catch (error: any) {
      console.error('Error loading QuickBooks status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load QuickBooks status',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Connect to QuickBooks - redirects to OAuth flow
  const handleConnect = async () => {
    try {
      setConnecting(true);
      // Get the QuickBooks authorization URL from backend
      const response = await apiRequest('/quickbooks/auth-url', { method: 'GET' });
      const { authUrl } = response.data;
      
      if (authUrl) {
        // Redirect directly to QuickBooks login page
        window.location.href = authUrl;
      } else {
        throw new Error('No auth URL received from server');
      }
    } catch (error: any) {
      console.error('Error getting QuickBooks auth URL:', error);
      setConnecting(false);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to connect to QuickBooks',
        severity: 'error',
      });
    }
  };

  // Disconnect QuickBooks
  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      await apiRequest('/quickbooks/disconnect', { method: 'POST' });
      setSnackbar({
        open: true,
        message: 'QuickBooks disconnected successfully',
        severity: 'success',
      });
      // Refresh status
      await loadStatus();
    } catch (error: any) {
      console.error('Error disconnecting QuickBooks:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to disconnect QuickBooks',
        severity: 'error',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Integrations
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Connect your external services to sync data and automate workflows
        </Typography>
      </Box>

      {/* QuickBooks Integration Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  QB
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" component="div">
                  QuickBooks Online
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sync approved quotes to QuickBooks as Estimates
                </Typography>
              </Box>
            </Box>
            {loading ? (
              <CircularProgress size={24} />
            ) : status?.connected ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Connected"
                color="success"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<ErrorIcon />}
                label="Not Connected"
                color="default"
                variant="outlined"
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : status?.connected ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                QuickBooks is connected and ready to sync quotes.
              </Alert>
              {status.realmId && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Realm ID:</strong> {status.realmId}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                When a quote is approved, it will automatically be created as an Estimate in
                QuickBooks.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Connect your QuickBooks account to automatically sync approved quotes as Estimates.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>How it works:</strong>
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Click "Connect QuickBooks" to authorize the connection
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    You'll be redirected to QuickBooks to log in and authorize the app
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="text.secondary">
                    Once connected, approved quotes will automatically sync to QuickBooks
                  </Typography>
                </li>
              </Box>
            </Box>
          )}
        </CardContent>
        {!canControl && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Alert severity="warning">
              You don't have permission to manage integrations. Contact Vladimir to make changes.
            </Alert>
          </Box>
        )}
        <CardActions sx={{ px: 2, pb: 2 }}>
          {status?.connected ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={handleDisconnect}
              disabled={disconnecting || !canControl}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect QuickBooks'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={handleConnect}
              disabled={connecting || !canControl}
            >
              {connecting ? 'Connecting...' : 'Connect QuickBooks'}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={loadStatus}
            disabled={loading}
            sx={{ ml: 1 }}
          >
            Refresh Status
          </Button>
        </CardActions>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
